import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg || JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      nav(from, { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-8 py-16" data-testid="login-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Sign in</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-8">
        Step back to the crease
      </h1>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Email" type="email" value={email} onChange={setEmail} testId="login-email" required />
        <Field label="Password" type="password" value={password} onChange={setPassword} testId="login-password" required />
        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit-btn"
          className="tactical-btn w-full bg-primary text-primary-foreground hover:bg-[#C9E620] disabled:opacity-50 px-6 py-4 inline-flex items-center justify-center gap-2 text-sm"
        >
          {busy ? "Signing in..." : "Sign In"} <ArrowRight className="w-4 h-4" strokeWidth={3} />
        </button>
      </form>
      <p className="text-sm text-muted-foreground mt-6">
        New here?{" "}
        <Link to="/register" className="text-primary hover:underline font-bold" data-testid="register-link">
          Create an account
        </Link>
      </p>
    </div>
  );
}

function Field({ label, type, value, onChange, testId, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        required={required}
        className="w-full bg-transparent border border-border focus:border-primary focus:outline-none px-4 py-3 mt-2 font-mono text-sm"
      />
    </div>
  );
}
